// Note: you must create this file, following the format of routingConfig.js.example
import routingConfig from './routingConfig.js';
import axios from 'axios';
import flexpolyline from './flexpolyline';
import { flip, pad2, featureCollection, feature, point, lineString } from './routingUtils';

export async function getRouteV7 ({
  from,
  waypoints,
  to,
  transportMode,
  timeDate,
  locale,
  extraAttributes = {}
}) {
  const makeRequest = async () => {
    let timeParam = {};
    let walkRadiusParam = {};
    if (transportMode.match(/publicTransport/)) {
      if (timeDate.time) {
        timeParam = {[timeDate.timeMode]: `${timeDate.date}T${timeDate.time}:00`};
      }
      walkRadiusParam = { walkRadius: 3000 }
    }
    const toGeo = point => `geo!${point.geometry.coordinates[1]},${point.geometry.coordinates[0]}`;
    const waypointParams = {
      waypoint0: toGeo(from)
    };
    waypoints.forEach((waypoint, i) => {
      waypointParams[`waypoint${i + 1}`] = toGeo(waypoint)
    });
    waypointParams[`waypoint${waypoints.length + 1}`] = toGeo(to);

    const mode = (transportMode === 'fastest;publicTransport' && timeDate.time ? 'fastest;publicTransportTimeTable' : transportMode);
    const alternatives = waypoints.length > 0 ? 0 : 5; // API doesn't support alternatives with waypoints
    const language = locale.match(/de/) ? 'de-de' : locale;
    const response = await axios.get(`https://route.ls.hereapi.com/routing/7.2/calculateroute.json`, {
      params: {
        apiKey: routingConfig.hereApiKey,
        ...waypointParams,
        mode,
        lineattributes: 'all',
        maneuverattributes: 'all', // TODO strip down to the ones we need.
        routeattributes: 'all',
        combineChange: true, // avoid separate "enter" and "leave" steps for interchanges
        ...timeParam,
        ...walkRadiusParam,
        language,
        alternatives,
        ...extraAttributes
      }
    });
    console.log(response);
    return response;
  }

  function stopsGeometryFromRoute (route) {
    const stops = [];
    for (const leg of route.leg) {
      const newStops = leg.maneuver
        .filter(m => ['change', 'enter', 'leave'].indexOf(m.action) >= 0);
      newStops.forEach((m, i) => { m.changeId = i + 1; });
      stops.push(...newStops);
    }

    return featureCollection(stops.map(m =>
      feature({ changeId: m.changeId }, point([m.position.longitude, m.position.latitude]))))
  }

  function shapeToLineFeature (shape, maneuver) {
    return feature({
      type: maneuver._type,
      instruction: maneuver.instruction.replace(/(<([^>]+)>)/g, '')
    }, lineString(shape.map(coordString => flip(coordString.split(',').map(Number))))
    );
  }

  function routeGeometryFromRoute (route) {
    const fc = featureCollection([]);
    for (const leg of route.leg) {
      const lineFeatures = leg.maneuver
        .filter(m => m.shape.length > 1)
        .map(m => shapeToLineFeature(m.shape, m));
      fc.features.push(...lineFeatures);
    }
    return fc;
  }

  function addCumulativeTimes (leg) {
    let total = 0;
    for (const maneuver of leg.maneuver) {
      maneuver.cumulative = `${pad2(Math.floor(total / 3600))}:${pad2(Math.floor(total / 60) % 60)}`;
      total += (maneuver.travelTime || 0);
    }
  }

  let result;
  const ret = {};
  try {
    result = await makeRequest();
    const route = result.data.response.route[0];
    route.leg.forEach(addCumulativeTimes);
    ret.route = route;
    ret.boundingBox = [route.boundingBox.topLeft.longitude, route.boundingBox.bottomRight.latitude, route.boundingBox.bottomRight.longitude, route.boundingBox.topLeft.latitude];
    ret.routeGeometry = routeGeometryFromRoute(route);
    ret.stopsGeometry = stopsGeometryFromRoute(route);
  } catch (e) {
    console.log(e);
    console.log(e.response, e.response.status, e.response.responseText, e.response.data.subtype);
    if (e.response && e.response.status === 400 && e.response.data.subtype === 'NoRouteFound') {
      ret.errorMessage = 'No route was found.';
    } else if (e.response && e.response.data && e.response.data.details === 'Time Table Transit Routing is only supported for two StopOver waypoints') {
      ret.errorMessage = 'Sorry, you can specify a time, or multiple waypoints, but not both.';
    } else {
      ret.errorMessage = 'Sorry, directions are currently unavailable.'
    }
    if (e.response && e.response.data && e.response.data.details) {
      console.log(e.response.data.details, e.response.data.additionalData);
    }
  }
  return ret;
}

export async function getRouteV8 ({
  from,
  to,
  waypoints,
  transportMode,
  locale,
  timeDate,
  extraAttributes = {}
}) {
  function polylineToGeoJSON (polyline, type) {
    const points = flexpolyline.decode(polyline).polyline
    return lineString(points.map(flip))
  }

  function addCumulativeTimes (section) {
    let total = 0;
    if (!section.actions) {
      return;
    }
    for (const action of section.actions) {
      action.cumulative = `${pad2(Math.floor(total / 3600))}:${pad2(Math.floor(total / 60) % 60)}`;
      total += (action.duration || 0);
    }
  }

  function stopsGeometryFromRoute (route) {
    const stops = [];
    for (const section of route.sections.slice(1)) {
      stops.push(section.departure.place.location);
    }

    return featureCollection(stops.map((m, id) =>
      feature({ changeId: id + 1 }, point([m.lng, m.lat]))))
  }

  // https://developer.here.com/documentation/routing-api/api-reference-swagger.html
  // https://developer.here.com/documentation/routing/dev_guide/topics/resource-calculate-route.html
  // https://developer.here.com/documentation/public-transit/dev_guide/routing/route-example.html

  const via = waypoints
    .filter(Boolean)
    .map(w => w.geometry.coordinates)
    .filter(Boolean)
    .map(flip)
    .map(c => c.join(','));
  let transitSubdomain = '';
  let routingMode, returnParam;
  let timeParam;
  if (transportMode.match(/transit|bus/)) {
    transitSubdomain = 'transit.';
    returnParam = 'polyline,actions,travelSummary';// don't need summary, instructions? not supported by transit
    if (timeDate && timeDate.time) {
      timeParam = {[timeDate.timeMode + 'Time']: `${timeDate.date}T${timeDate.time}:00`}; // departureTime, arrivalTime
    }
  } else {
    routingMode = transportMode.split('-')[1];
    transportMode = transportMode === 'bus' ? 'bus' : 'car';
    returnParam = 'polyline,actions,summary,instructions';
  }

  const params = {
    origin: flip(from.geometry.coordinates).join(','),
    destination: flip(to.geometry.coordinates).join(','),
    ...(via ? { via } : {}),
    return: returnParam,
    apiKey: routingConfig.hereApiKey,
    lang: locale,
    ...(routingMode ? { routingMode } : undefined),
    ...(transportMode ? { transportMode } : undefined),
    ...timeParam,
    ...extraAttributes,
    ...(transportMode === 'bus' ? { alternatives: 6 } : {})
  }
  transportMode = transportMode === 'bus' ? params.transportMode = 'transit' : transportMode
  const result = await axios.get(`https://${transitSubdomain}router.hereapi.com/v8/routes`, {
    params,
    // the API wants &via=x,y&via=x,y whereas Axios by default provides &via[]=x,y&via[]=x,y
    paramsSerializer: params =>
      Object.keys(params)
        .map(key => {
          const vals = Array.isArray(params[key]) ? params[key] : [params[key]];
          return vals.map(val => `${key}=${val}`).join('&')
        }).join('&')
  });
  let route = null;
  if (params.alternatives) {
    const values = [{name: 'HS 1'}, {name: 'HS 2'}, {name: 'HS 3'}, {name: 'HS 4'}, {name: 'HS 5'}];
    let response = result.data.routes.filter(data => {
      if (data.sections[1]) {
        return values.some(r => {
          return data.sections[1].transport.name === r.name
        })
      } else {
        return []
      }
    })
    route = response[0]
    // const allDurations = result.data.routes.map(r => r.sections[0].summary.duration);
    // const minDuration = Math.min.apply(null, allDurations);
    // route = result.data.routes.find(r => r.sections[0].summary.duration === minDuration);
  } else {
    route = result.data.routes[0]
  }
  if (route !== undefined) {
    route.sections.forEach(addCumulativeTimes);
    return {
      route,
      routeGeometry: {
        type: 'FeatureCollection',
        features: route.sections.map(section => ({
          type: 'Feature',
          properties: {
            type: section.type
          },
          geometry: polylineToGeoJSON(section.polyline)
        }))
      },
      stopsGeometry: stopsGeometryFromRoute(route)
    }
  } else {
    return 'No route found.'
  }
}



// WEBPACK FOOTER //
// ./src/components/routing/hereRoutingApi.js